import type { ReactNode } from 'react'
import type { Tone } from '@/constants/clinic'
import type {
    AppointmentStatus,
    FollowUpBucket,
    MessageStatus,
    PatientStatus,
    PaymentStatus
} from '@/types/clinic'

import { cn } from '@/lib/utils'

import {
    APPOINTMENT_STATUS_META,
    FOLLOW_UP_BUCKET_META,
    MESSAGE_STATUS_META,
    PATIENT_STATUS_META,
    PAYMENT_STATUS_META
} from '@/constants/clinic'

export const TONE_CLASSES: Record<Tone, { badge: string, dot: string, soft: string, icon: string }> = {
    slate: { badge: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-400', soft: 'bg-slate-50', icon: 'bg-slate-100 text-slate-600' },
    teal: { badge: 'bg-teal-50 text-teal-800 ring-teal-200', dot: 'bg-teal-500', soft: 'bg-teal-50/60', icon: 'bg-teal-50 text-teal-700' },
    blue: { badge: 'bg-sky-50 text-sky-800 ring-sky-200', dot: 'bg-sky-500', soft: 'bg-sky-50/60', icon: 'bg-sky-50 text-sky-700' },
    green: { badge: 'bg-emerald-50 text-emerald-800 ring-emerald-200', dot: 'bg-emerald-500', soft: 'bg-emerald-50/60', icon: 'bg-emerald-50 text-emerald-700' },
    amber: { badge: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500', soft: 'bg-amber-50/60', icon: 'bg-amber-50 text-amber-700' },
    red: { badge: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500', soft: 'bg-rose-50/60', icon: 'bg-rose-50 text-rose-600' },
    orange: { badge: 'bg-orange-50 text-orange-800 ring-orange-200', dot: 'bg-orange-500', soft: 'bg-orange-50/60', icon: 'bg-orange-50 text-orange-700' },
    violet: { badge: 'bg-violet-50 text-violet-700 ring-violet-200', dot: 'bg-violet-500', soft: 'bg-violet-50/60', icon: 'bg-violet-50 text-violet-700' }
}

interface StatusBadgeProps {
    tone: Tone
    label: string
    className?: string
}

export const StatusBadge = ({ tone, label, className }: StatusBadgeProps): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
                TONE_CLASSES[tone].badge,
                className
            )}
        >
            <span aria-hidden='true' className={cn('size-1.5 rounded-full', TONE_CLASSES[tone].dot)} />
            {label}
        </span>
    )
}

export const AppointmentStatusBadge = ({ status, className }: { status: AppointmentStatus, className?: string }): ReactNode => {
    const meta = APPOINTMENT_STATUS_META[status]
    return <StatusBadge tone={meta.tone} label={meta.label} className={className} />
}

export const PaymentStatusBadge = ({ status, className }: { status: PaymentStatus, className?: string }): ReactNode => {
    const meta = PAYMENT_STATUS_META[status]
    return <StatusBadge tone={meta.tone} label={meta.label} className={className} />
}

export const PatientStatusBadge = ({ status, className }: { status: PatientStatus, className?: string }): ReactNode => {
    const meta = PATIENT_STATUS_META[status] ?? PATIENT_STATUS_META.active
    return <StatusBadge tone={meta.tone} label={meta.label} className={className} />
}

export const FollowUpBadge = ({ bucket, className }: { bucket: FollowUpBucket, className?: string }): ReactNode => {
    const meta = FOLLOW_UP_BUCKET_META[bucket]
    return <StatusBadge tone={meta.tone} label={meta.label} className={className} />
}

export const MessageStatusBadge = ({ status, className }: { status: MessageStatus, className?: string }): ReactNode => {
    const meta = MESSAGE_STATUS_META[status]
    return <StatusBadge tone={meta.tone} label={meta.label} className={className} />
}
